import axios from "axios";
import { getAuthToken, removeAuthToken } from "./utils/session";

const apiClient = axios.create({
  baseURL:
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    "https://laplage-core.landmarkcooperative.org",
  timeout: 30000,
});

// Endpoints that must never receive an Authorization header
const PUBLIC_ENDPOINTS = [
  "/student/authenticate",
  "/student/createStudent",
  "/student/createStudentAndCourse",
  "/student/resetStudentPassword",
  "/student/resetStudentPasswordWithYearOfBirth",
];

const isPublicEndpoint = (url?: string) =>
  !!url && PUBLIC_ENDPOINTS.some((p) => url.startsWith(p));

// Add request interceptor to include authorization token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token && !isPublicEndpoint(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      // Delete Content-Type header for FormData - axios will automatically set multipart/form-data with proper boundary
      delete config.headers["Content-Type"];
    } else {
      // For non-FormData requests, set application/json
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data;
    const msg = typeof data === "string" ? data : "";

    // Backend returns 400 "Invalid JWT token" for bad tokens, so clear it
    // on that too, not just on 401
    if (
      error.response?.status === 400 &&
      /invalid jwt/i.test(msg) &&
      typeof window !== "undefined"
    ) {
      removeAuthToken();
    }

    if (error.response?.status === 401 && typeof window !== "undefined") {
      // Expired / invalid token: always clear it
      removeAuthToken();
      // Only bounce to sign-in from protected pages. Public pages (landing,
      // school, course pages) also call authed endpoints for the header, and
      // an expired cookie there should just mean "logged out", not a redirect.
      if (window.location.pathname.startsWith("/dashboard")) {
        window.location.href = "/sign-in";
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
